import { generateBreadcrumbs } from '@/utilities/router/generate-breadcrumbs';
import { generateHeader } from '@/utilities/router/generate-header';

import sidebarItems from './sidebar';

import type { RouteMeta, RouteRecordRaw } from '@bv/router';

const callCenterBreadcrumbs = generateBreadcrumbs({
  label: 'Call center',
  route: { name: 'cc' },
});

const callCenterServersBreadcrumbs = generateBreadcrumbs(
  { label: 'Call center', route: { name: 'cc' } },
  { label: 'Servers', route: { name: 'cc-servers-list' } },
);

const callCenterRoutes: RouteRecordRaw = {
  path: 'call-center',
  name: 'cc',
  meta: {
    sidebar: sidebarItems,
    header: generateHeader('Call center'),
  },
  redirect: { name: 'cc-servers-list' },
  children: [
    {
      path: 'sync',
      meta: {
        breadcrumbs: callCenterBreadcrumbs({
          label: 'Sync',
          route: { name: 'cc-sync-history' },
        }),
      },
      children: [
        {
          path: '',
          name: 'cc-sync-history',
          component: () => import('@/components/Synchronization/SyncHistory.vue'),
        },
        {
          path: ':historyId/:serverId/:server/:inspection',
          name: 'cc-sync-show',
          component: () => import('@/components/Synchronization/SyncDetails.vue'),
        },
      ],
    },
    {
      path: 'servers',
      meta: {
        breadcrumbs: callCenterBreadcrumbs({
          label: 'Servers',
          route: { name: 'cc-servers-list' },
        }),
        redirectUnauthorized: { name: 'cc-servers-list' },
      },
      children: [
        {
          path: '',
          name: 'cc-servers-list',
          component: () => import('@/views/Main/Servers/List.vue'),
        },
        {
          path: '',
          component: () => import('@/views/Main/Servers/Form.vue'),
          children: [
            {
              path: 'add',
              name: 'cc-servers-add',
              meta: {
                permissions: 'write-servers',
              },
            },
            {
              path: ':id/edit',
              name: 'cc-servers-edit',
            },
          ] as RouteRecordRaw[],
        },
        {
          path: ':id',
          name: 'cc-servers-config',
          component: () => import('@/views/Main/Servers/Config.vue'),
          meta: {
            permissions: 'config-servers',
          },
        },
        {
          path: ':id',
          meta: {
            permissions: 'config-servers',
          },
          children: [
            {
              path: 'extensions',
              meta: {
                breadcrumbs: callCenterServersBreadcrumbs({
                  label: 'Extensions',
                  route: { name: 'cc-extensions-list' },
                }),
              },
              children: [
                {
                  path: '',
                  name: 'cc-extensions-list',
                  component: () => import('@/components/Extensions/ExtensionsList.vue'),
                },
                {
                  path: '',
                  component: () => import('@/components/Extensions/ExtensionForm.vue'),
                  children: [
                    {
                      path: 'add',
                      children: [
                        {
                          path: 'sip',
                          name: 'cc-extensions-add-sip',
                          meta: { type: 'add', category: 'sip' } as RouteMeta,
                        },
                        {
                          path: 'pjsip',
                          name: 'cc-extensions-add-pjsip',
                          meta: { type: 'add', category: 'pjsip' } as RouteMeta,
                        },
                      ] as RouteRecordRaw[],
                    },
                    {
                      path: 'edit/:extensionId/:workspaceId',
                      children: [
                        {
                          path: 'sip',
                          name: 'cc-extensions-edit-sip',
                          meta: { type: 'edit', category: 'sip' } as RouteMeta,
                        },
                        {
                          path: 'pjsip',
                          name: 'cc-extensions-edit-pjsip',
                          meta: { type: 'edit', category: 'pjsip' } as RouteMeta,
                        },
                      ] as RouteRecordRaw[],
                    },
                  ] as RouteRecordRaw[],
                },
              ],
            },
            {
              path: 'inbound-routes',
              meta: {
                breadcrumbs: callCenterServersBreadcrumbs({
                  label: 'Inbound routes',
                  route: { name: 'cc-inbound-routes-list' },
                }),
              },
              children: [
                {
                  path: '',
                  name: 'cc-inbound-routes-list',
                  component: () => import('@/components/Inbounds/List.vue'),
                },
                {
                  path: 'add',
                  name: 'cc-inbound-routes-add',
                  component: () => import('@/components/Inbounds/Form.vue'),
                },
                {
                  path: 'edit/:inboundId/:workspaceId',
                  name: 'cc-inbound-routes-edit',
                  component: () => import('@/components/Inbounds/Form.vue'),
                },
              ],
            },
            {
              path: 'outbound-routes',
              meta: {
                breadcrumbs: callCenterServersBreadcrumbs({
                  label: 'Outbound routes',
                  route: { name: 'cc-outbound-routes-list' },
                }),
              },
              children: [
                {
                  path: '',
                  name: 'cc-outbound-routes-list',
                  component: () => import('@/components/OutboundRoutes/List.vue'),
                },
                {
                  path: 'add',
                  name: 'cc-outbound-routes-add',
                  component: () => import('@/components/OutboundRoutes/Form.vue'),
                },
                {
                  path: 'edit/:outboundId/:workspaceId',
                  name: 'cc-outbound-routes-edit',
                  component: () => import('@/components/OutboundRoutes/Form.vue'),
                },
              ],
            },
            {
              path: 'custom-destinations',
              meta: {
                breadcrumbs: callCenterServersBreadcrumbs({
                  label: 'Custom destinations',
                  route: { name: 'cc-custom-destinations-list' },
                }),
              },
              children: [
                {
                  path: '',
                  name: 'cc-custom-destinations-list',
                  component: () => import('@/components/cc/servers/config/CustomDestinations/List.vue'),
                },
                {
                  path: 'add',
                  name: 'cc-custom-destinations-add',
                  component: () => import('@/components/cc/servers/config/CustomDestinations/Form.vue'),
                },
                {
                  path: 'edit/:customDestinationId/:workspaceId',
                  name: 'cc-custom-destinations-edit',
                  component: () => import('@/components/cc/servers/config/CustomDestinations/Form.vue'),
                },
              ],
            },
            {
              path: 'trunks',
              meta: {
                breadcrumbs: callCenterServersBreadcrumbs({
                  label: 'Trunks',
                  route: { name: 'cc-trunks-list' },
                }),
              },
              children: [
                {
                  path: '',
                  name: 'cc-trunks-list',
                  component: () => import('@/components/cc/servers/config/Trunks/List.vue'),
                },
                {
                  path: '',
                  component: () => import('@/components/cc/servers/config/Trunks/Form.vue'),
                  children: [
                    {
                      path: 'add',
                      name: 'cc-trunks-add',
                      children: [
                        {
                          path: 'pjsip',
                          name: 'cc-trunks-add-pjsip',
                        },
                        {
                          path: 'sip',
                          name: 'cc-trunks-add-sip',
                        },
                      ] as RouteRecordRaw[],
                    },
                    {
                      path: 'edit/:trunkId',
                      name: 'cc-trunks-edit',
                      children: [
                        {
                          path: 'pjsip',
                          name: 'cc-trunks-edit-pjsip',
                        },
                        {
                          path: 'sip',
                          name: 'cc-trunks-edit-sip',
                        },
                      ] as RouteRecordRaw[],
                    },
                  ],
                },
              ],
            },
            {
              path: 'queues',
              meta: {
                breadcrumbs: callCenterServersBreadcrumbs({
                  label: 'Queues',
                  route: { name: 'cc-queues-list' },
                }),
              },
              children: [
                {
                  path: '',
                  name: 'cc-queues-list',
                  component: () => import('@/views/Main/Settings/Queues/QueuesList/QueuesList.vue'),
                },
                {
                  path: 'add',
                  name: 'cc-queues-add',
                  component: () => import('@/views/Main/Settings/Queues/AddQueues.vue'),
                },
                {
                  path: 'edit/:workspaceId/:queueId',
                  name: 'cc-queues-edit',
                  component: () => import('@/views/Main/Settings/Queues/QueuesList/EditQueue.vue'),
                },
              ],
            },
            {
              path: 'recordings',
              meta: {
                breadcrumbs: callCenterServersBreadcrumbs({
                  label: 'Recordings',
                  route: { name: 'cc-recordings-list' },
                }),
              },
              children: [
                {
                  path: '',
                  name: 'cc-recordings-list',
                  component: () => import('@/views/Main/Settings/Recordings/List.vue'),
                },
                {
                  path: '',
                  component: () => import('@/views/Main/Settings/Recordings/Form.vue'),
                  children: [
                    {
                      path: 'add',
                      name: 'cc-recordings-add',
                    },
                    {
                      path: 'edit/:recordingId',
                      name: 'cc-recordings-edit',
                    },
                  ] as RouteRecordRaw[],
                },
              ],
            },
            {
              path: 'announcement',
              meta: {
                breadcrumbs: callCenterServersBreadcrumbs({
                  label: 'Announcement',
                  route: { name: 'cc-announcements-list' },
                }),
              },
              children: [
                {
                  path: '',
                  name: 'cc-announcements-list',
                  component: () => import('@/views/Main/Settings/Announcement/AnnouncementList/AnnouncementList.vue'),
                },
                {
                  path: 'add',
                  name: 'cc-announcements-add',
                  component: () => import('@/views/Main/Settings/Announcement/AddAnnouncement.vue'),
                },
                {
                  path: 'edit/:announcementId/:workspaceId',
                  name: 'cc-announcements-edit',
                  component: () => import('@/views/Main/Settings/Announcement/AnnouncementList/EditAnnouncement.vue'),
                },
              ],
            },
            {
              path: 'ivr',
              meta: {
                breadcrumbs: callCenterServersBreadcrumbs({
                  label: 'IVR',
                  route: { name: 'cc-ivr-list' },
                }),
              },
              children: [
                {
                  path: '',
                  name: 'cc-ivr-list',
                  component: () => import('@/views/Main/Settings/IVR/List.vue'),
                },
                {
                  path: '',
                  component: () => import('@/views/Main/Settings/IVR/Form.vue'),
                  children: [
                    {
                      path: 'add',
                      name: 'cc-ivr-add',
                    },
                    {
                      path: 'edit/:ivrId/:workspaceId',
                      name: 'cc-ivr-edit',
                    },
                  ] as RouteRecordRaw[],
                },
              ],
            },
            {
              path: 'sound-on-hold',
              meta: {
                breadcrumbs: callCenterServersBreadcrumbs({
                  label: 'Sound on hold',
                  route: { name: 'cc-sound-on-hold-list' },
                }),
              },
              children: [
                {
                  path: '',
                  name: 'cc-sound-on-hold-list',
                  component: () => import('@/views/Main/Settings/SoundOnHold/List.vue'),
                },
                {
                  path: '',
                  component: () => import('@/views/Main/Settings/SoundOnHold/Form.vue'),
                  children: [
                    {
                      path: 'add',
                      name: 'cc-sound-on-hold-add',
                    },
                    {
                      path: 'edit/:workspaceId/:soundOnHoldId',
                      name: 'cc-sound-on-hold-edit',
                    },
                  ] as RouteRecordRaw[],
                },
              ],
            },
            {
              path: 'time-conditions',
              meta: {
                breadcrumbs: callCenterServersBreadcrumbs({
                  label: 'Time conditions',
                  route: { name: 'cc-time-conditions-list' },
                }),
              },
              children: [
                {
                  path: '',
                  name: 'cc-time-conditions-list',
                  component: () =>
                    import('@/views/Main/Settings/TimeConditions/TimeConditionsList/TimeConditionsList.vue'),
                },
                {
                  path: 'add',
                  name: 'cc-time-conditions-add',
                  component: () => import('@/views/Main/Settings/TimeConditions/AddTimeCondition.vue'),
                },
                {
                  path: 'edit/:timeConditionId',
                  name: 'cc-time-conditions-edit',
                  component: () =>
                    import('@/views/Main/Settings/TimeConditions/TimeConditionsList/EditTimeConditions.vue'),
                },
              ],
            },
            {
              path: 'time-groups',
              meta: {
                breadcrumbs: callCenterServersBreadcrumbs({
                  label: 'Time groups',
                  route: { name: 'cc-time-groups-list' },
                }),
              },
              children: [
                {
                  path: '',
                  name: 'cc-time-groups-list',
                  component: () => import('@/views/Main/Settings/TimeGroups/TimeGroups/TimeGroups.vue'),
                },
                {
                  path: 'edit/:timeGroupId',
                  name: 'cc-time-groups-add',
                  component: () => import('@/views/Main/Settings/TimeGroups/TimeGroups/EditTimeGroup.vue'),
                },
                {
                  path: 'add',
                  name: 'cc-time-groups-edit',
                  component: () => import('@/views/Main/Settings/TimeGroups/AddTimeGroup.vue'),
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

export default callCenterRoutes;
